import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Users } from "lucide-react";
import MarketingLayout from "../../components/marketing/MarketingLayout";
import Seo from "../../components/Seo";
import { SYSTEM_PILLARS, PROCESS, MODULES, IMPACTS, INDUSTRY_CARDS } from "../../data/site";

const Section = ({ children, className = "" }) => (
  <section className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 ${className}`}>{children}</section>
);

const FlowArrow = () => <span className="text-alto-red font-bold mx-1">→</span>;

export default function Home() {
  return (
    <MarketingLayout>
      <Seo title="AllToMAIDIt — Stop Losing the Leads You Already Paid For" description="AllToMAIDIt connects your website, CRM, marketing and follow-up into one system designed to turn more opportunities into customers." />

      {/* 1 — HERO */}
      <div className="relative overflow-hidden marketing-grid-bg border-b border-white/10">
        <Section className="pt-20 pb-24 lg:pt-28 lg:pb-32">
          <p className="eyebrow text-alto-red mb-5 anim-rise">The AllToMAIDIt Lead-to-Client System</p>
          <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[0.95] anim-rise max-w-4xl">
            STOP LOSING THE LEADS<br /><span className="text-alto-red">YOU ALREADY PAID FOR.</span>
          </h1>
          <p className="text-base sm:text-lg text-neutral-300 mt-7 max-w-2xl leading-relaxed anim-rise">
            AllToMAIDIt connects your website, CRM, marketing and follow-up into one system designed to turn more opportunities into customers.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 mt-9 anim-rise">
            <Link to="/lead-system-audit" data-testid="hero-primary-cta" className="btn-red font-bold px-7 py-4 rounded-md inline-flex items-center justify-center gap-2 text-sm">
              GET YOUR FREE LEAD SYSTEM AUDIT <ArrowRight size={18} />
            </Link>
            <Link to="/solutions/lead-to-client" data-testid="hero-secondary-cta" className="font-bold px-7 py-4 rounded-md border border-white/20 hover:border-alto-red hover:text-alto-red transition-colors inline-flex items-center justify-center gap-2 text-sm">
              SEE HOW IT WORKS
            </Link>
          </div>
          <div className="mt-12 flex flex-wrap items-center gap-y-2 text-sm sm:text-base font-display font-bold text-neutral-400 anim-rise">
            <span className="text-white">Lead</span><FlowArrow /><span>Capture</span><FlowArrow /><span>Follow-Up</span><FlowArrow /><span>Appointment</span><FlowArrow /><span className="text-alto-red">Customer</span>
          </div>
        </Section>
      </div>

      {/* 2 — PROBLEM */}
      <Section className="py-20 lg:py-24">
        <p className="eyebrow text-alto-red mb-3">The Problem</p>
        <h2 className="font-display text-3xl sm:text-4xl font-bold max-w-2xl">Getting the lead is only half the battle.</h2>
        <div className="grid lg:grid-cols-2 gap-10 mt-10 items-center">
          <div className="space-y-4 text-neutral-300">
            {["You paid for the ad.", "Someone visited your website.", "They submitted the form.", "Then what?"].map((t, i) => (
              <p key={i} className={`text-lg ${i === 3 ? "text-alto-red font-bold" : ""}`}>{t}</p>
            ))}
            <p className="text-neutral-400 text-sm pt-2">If follow-up is slow, inconsistent or forgotten, the money spent generating that lead is wasted.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="rounded-xl p-6 bg-alto-charcoal border border-white/10">
              <p className="eyebrow text-neutral-500 mb-4">Without a system</p>
              <div className="font-display font-bold text-neutral-300 space-y-1 text-sm">
                <p>Ads / Google / Social / Referrals</p><p className="text-neutral-600">↓</p><p>Leads</p><p className="text-neutral-600">↓</p><p className="text-alto-red text-2xl">???</p>
              </div>
            </div>
            <div className="rounded-xl p-6 bg-black border border-alto-red/40">
              <p className="eyebrow text-alto-red mb-4">With AllToMAIDIt</p>
              <div className="font-display font-bold text-white space-y-1 text-sm">
                {["Traffic", "Capture", "CRM", "Follow-Up", "Appointment"].map((s) => (<React.Fragment key={s}><p>{s}</p><p className="text-neutral-600">↓</p></React.Fragment>))}
                <p className="text-alto-red">Customer</p>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* 3 — SOLUTION */}
      <div className="border-y border-white/10 bg-alto-panel">
        <Section className="py-20 lg:py-24">
          <p className="eyebrow text-alto-red mb-3">The Solution</p>
          <h2 className="font-display text-3xl sm:text-4xl font-bold mb-12 max-w-3xl">One system. From first click to closed customer.</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {SYSTEM_PILLARS.map((p) => (
              <div key={p.key} className="card-hairline rounded-xl p-6 bg-black" data-testid={`pillar-${p.key}`}>
                <p.icon className="text-alto-red mb-4" size={26} />
                <h3 className="font-display text-lg font-extrabold tracking-wide">{p.title}</h3>
                <ul className="mt-3 space-y-1.5">{p.items.map((i) => <li key={i} className="text-sm text-neutral-400 flex items-center gap-2"><span className="w-1 h-1 bg-alto-red rounded-full" />{i}</li>)}</ul>
              </div>
            ))}
          </div>
        </Section>
      </div>

      {/* 4 — FLAGSHIP PRODUCT */}
      <Section className="py-20 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="eyebrow text-alto-red mb-4">Powered by AllToMAIDIt CRM</p>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold leading-tight">Your lead system is powered by our own CRM.</h2>
            <p className="text-neutral-300 mt-5 leading-relaxed">Built around one simple principle: <span className="text-white font-semibold">no opportunity should disappear because somebody forgot to follow up.</span></p>
            <Link to="/products/crm" data-testid="home-explore-crm" className="btn-red font-bold px-6 py-3.5 rounded-md inline-flex items-center gap-2 mt-8 text-sm">EXPLORE THE CRM <ArrowRight size={16} /></Link>
          </div>
          <div className="card-hairline rounded-2xl p-6 bg-alto-charcoal">
            <div className="flex items-center gap-2 text-alto-red mb-4"><Users size={18} /><span className="eyebrow">Needs Attention</span></div>
            {["4 new leads have not received first follow-up", "3 follow-ups are overdue", "2 website inquiries have no assigned owner", "1 lead has no next action"].map((r, i) => (
              <div key={i} className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
                <span className="text-sm text-neutral-200">{r}</span><span className={`w-2 h-2 rounded-full ${i < 2 ? "bg-alto-red" : "bg-amber-500"}`} />
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* 5 — HOW IT WORKS */}
      <div className="border-y border-white/10 bg-alto-panel">
        <Section className="py-20 lg:py-24">
          <p className="eyebrow text-alto-red mb-3">How It Works</p>
          <h2 className="font-display text-3xl sm:text-4xl font-bold mb-12">Four steps to a system that works.</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PROCESS.map((h) => (
              <div key={h.step} className="rounded-xl p-6 bg-black border border-white/10">
                <span className="font-mono text-alto-red text-sm">{h.step}</span>
                <h.icon className="text-white my-4" size={24} />
                <h3 className="font-display text-lg font-bold mb-2">{h.title}</h3>
                <p className="text-sm text-neutral-400">{h.desc}</p>
              </div>
            ))}
          </div>
          <Link to="/lead-system-audit" className="btn-red font-bold px-7 py-4 rounded-md inline-flex items-center gap-2 mt-10 text-sm">START WITH A FREE AUDIT <ArrowRight size={18} /></Link>
        </Section>
      </div>

      {/* 6 — MODULES */}
      <Section className="py-20 lg:py-24">
        <p className="eyebrow text-alto-red mb-3">What Can Be Included</p>
        <h2 className="font-display text-3xl sm:text-4xl font-bold mb-3 max-w-2xl">Build the right system — not every feature.</h2>
        <p className="text-neutral-400 text-sm mb-10 max-w-xl">Not every business needs every module. We assemble the appropriate system around your needs.</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
          {MODULES.map((m) => (
            <div key={m.name} className="rounded-xl p-4 bg-alto-charcoal border border-white/10 flex flex-col items-center text-center gap-2 hover:border-alto-red/50 transition-colors">
              <m.icon className="text-alto-red" size={22} /><span className="text-xs text-neutral-300 font-medium">{m.name}</span>
            </div>
          ))}
        </div>
      </Section>

      {/* 7 — BUSINESS IMPACT */}
      <div className="border-y border-white/10 bg-gradient-to-b from-black to-alto-panel">
        <Section className="py-20 lg:py-24">
          <p className="eyebrow text-alto-red mb-3">Business Impact</p>
          <h2 className="font-display text-3xl sm:text-4xl font-bold mb-12">What changes when the system is in place.</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {IMPACTS.map((i) => (
              <div key={i.title} className="flex items-center gap-4 rounded-xl p-5 bg-black border border-white/10">
                <i.icon className="text-alto-red shrink-0" size={24} /><span className="font-display font-bold">{i.title}</span>
              </div>
            ))}
          </div>
        </Section>
      </div>

      {/* 8 — WHO IT'S FOR */}
      <Section className="py-20 lg:py-24">
        <p className="eyebrow text-alto-red mb-3">Who It's For</p>
        <h2 className="font-display text-3xl sm:text-4xl font-bold mb-12">Built for lead-driven businesses.</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {INDUSTRY_CARDS.map((c) => (
            <Link key={c.slug} to="/industries" className="card-hairline rounded-xl p-6 bg-alto-charcoal group">
              <c.icon className="text-alto-red mb-4" size={26} />
              <h3 className="font-display font-bold group-hover:text-alto-red transition-colors">{c.name}</h3>
              <p className="text-xs text-neutral-400 mt-2">{c.desc}</p>
            </Link>
          ))}
        </div>
      </Section>

      {/* 9 — CRM OPTION */}
      <div className="border-y border-white/10 bg-alto-panel">
        <Section className="py-16 lg:py-20">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
            <div>
              <h2 className="font-display text-2xl sm:text-3xl font-bold">Just need the CRM?</h2>
              <p className="text-neutral-400 mt-2 text-sm max-w-xl">Already have your marketing and website? Get AllToMAIDIt CRM on its own — Start, Grow or Scale.</p>
            </div>
            <Link to="/products/crm" className="btn-red font-bold px-6 py-3.5 rounded-md inline-flex items-center gap-2 text-sm shrink-0">EXPLORE CRM PLANS <ArrowRight size={16} /></Link>
          </div>
        </Section>
      </div>

      {/* 10 — SERVICES */}
      <Section className="py-16 lg:py-20">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
          <div>
            <p className="eyebrow text-alto-red mb-3">Services</p>
            <h2 className="font-display text-2xl sm:text-3xl font-bold">Need a single capability instead?</h2>
            <p className="text-neutral-400 mt-2 text-sm max-w-xl">We also provide individual technology services — web, AI & automation, marketing, branding, IT and integrations.</p>
          </div>
          <Link to="/services" className="font-bold px-6 py-3.5 rounded-md border border-white/20 hover:border-alto-red hover:text-alto-red transition-colors text-sm shrink-0">EXPLORE SERVICES</Link>
        </div>
      </Section>

      {/* 11 — FINAL CTA */}
      <div className="border-t border-white/10 bg-gradient-to-br from-alto-red/15 to-black">
        <Section className="py-20 lg:py-28 text-center">
          <h2 className="font-display text-3xl sm:text-5xl font-extrabold max-w-3xl mx-auto leading-tight">How many leads are falling through the cracks?</h2>
          <p className="text-neutral-300 mt-5 max-w-xl mx-auto text-sm">Find out where your current lead process is working — and where opportunities may be getting lost.</p>
          <Link to="/lead-system-audit" data-testid="final-primary-cta" className="btn-red font-bold px-8 py-4 rounded-md inline-flex items-center justify-center gap-2 mt-9 text-sm">GET YOUR FREE LEAD SYSTEM AUDIT <ArrowRight size={18} /></Link>
        </Section>
      </div>
    </MarketingLayout>
  );
}
